/**
 * Price Monitor API Client
 * Handles all API calls for the Price Monitor module
 */

import apiClient from './client';

export interface MonitoredProduct {
    id: number;
    product_id: number;
    variation_id: number | null;
    product_name: string;
    parent_product: string | null;
    sku: string | null;
    current_regular_price: number;
    current_sale_price: number | null;
    current_stock_quantity: number;
    is_monitoring_enabled: boolean;
    baseline_regular_price: number | null;
    baseline_sale_price: number | null;
    threshold_percentage: number | null;
    is_auto_disabled: boolean;
    auto_disabled_at: string | null;
    auto_disabled_reason: string | null;
}

export interface MonitoredProductsResponse {
    monitored: MonitoredProduct[];
    not_monitored: MonitoredProduct[];
    auto_disabled: MonitoredProduct[];
    total_count: number;
}

export interface PriceDropAlert {
    id: number;
    baseline_id: number;
    product_id: number;
    variation_id: number | null;
    product_name: string;
    parent_product: string | null;
    sku: string | null;
    baseline_regular_price: number;
    baseline_sale_price: number | null;
    detected_regular_price: number;
    detected_sale_price: number | null;
    regular_price_drop_percentage: number | null;
    sale_price_drop_percentage: number | null;
    triggered_by: string;
    action_taken: string;
    stock_set_to: number | null;
    alert_status: string;
    acknowledged_by: string | null;
    acknowledged_by_email: string | null;
    acknowledged_at: string | null;
    resolution_notes: string | null;
    created_at: string;
    updated_at: string;
}

export interface ScanResult {
    scan_id: string;
    products_scanned: number;
    alerts_triggered: number;
    products_disabled: number;
    errors_encountered: number;
    duration_seconds: number;
    scan_status: string;
    error_message: string | null;
}

export interface ScanHistoryItem {
    id: number;
    scan_id: string;
    products_scanned: number;
    alerts_triggered: number;
    products_disabled: number;
    errors_encountered: number;
    scan_type: string;
    scan_status: string;
    error_message: string | null;
    started_at: string;
    completed_at: string | null;
    duration_seconds: number | null;
    triggered_by_email: string | null;
}

export interface MonitoringStatistics {
    total_products: number;
    monitored_products: number;
    not_monitored_products: number;
    auto_disabled_products: number;
    active_alerts: number;
    total_alerts_24h: number;
    last_scan_time: string | null;
    last_scan_status: string | null;
}

export const priceMonitorAPI = {
    // Get all products with monitoring status
    getProducts: async (): Promise<MonitoredProductsResponse> => {
        const response = await apiClient.get('/price-monitor/products');
        return response.data;
    },

    // Enable monitoring for a product
    enableMonitoring: async (
        productId: number,
        variationId: number | null,
        thresholdPercentage: number = 25
    ): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post('/price-monitor/enable', {
            product_id: productId,
            variation_id: variationId,
            threshold_percentage: thresholdPercentage,
        });
        return response.data;
    },

    // Disable monitoring for a product
    disableMonitoring: async (
        productId: number,
        variationId: number | null
    ): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post('/price-monitor/disable', {
            product_id: productId,
            variation_id: variationId,
        });
        return response.data;
    },

    // Update threshold for a monitored product
    updateThreshold: async (
        productId: number,
        variationId: number | null,
        thresholdPercentage: number
    ): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post('/price-monitor/update-threshold', {
            product_id: productId,
            variation_id: variationId,
            threshold_percentage: thresholdPercentage,
        });
        return response.data;
    },

    // Reset baseline for a product
    resetBaseline: async (
        productId: number,
        variationId: number | null
    ): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post('/price-monitor/reset-baseline', {
            product_id: productId,
            variation_id: variationId,
        });
        return response.data;
    },

    // Reset all baselines to current prices
    resetAllBaselines: async (): Promise<{ success: boolean; message: string; count: number }> => {
        const response = await apiClient.post('/price-monitor/reset-all-baselines');
        return response.data;
    },

    // Re-enable a disabled product
    reEnableProduct: async (
        productId: number,
        variationId: number | null,
        newStockQuantity: number,
        resetBaseline: boolean = false
    ): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post('/price-monitor/re-enable', {
            product_id: productId,
            variation_id: variationId,
            new_stock_quantity: newStockQuantity,
            reset_baseline: resetBaseline,
        });
        return response.data;
    },

    // Bulk enable monitoring for multiple products
    bulkEnableMonitoring: async (
        productIds: Array<{ product_id: number; variation_id: number | null }>,
        thresholdPercentage: number = 25
    ): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post('/price-monitor/bulk-enable', {
            product_ids: productIds,
            threshold_percentage: thresholdPercentage,
        });
        return response.data;
    },

    // Bulk disable monitoring for multiple products
    bulkDisableMonitoring: async (
        productIds: Array<{ product_id: number; variation_id: number | null }>
    ): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post('/price-monitor/bulk-disable', {
            product_ids: productIds,
        });
        return response.data;
    },

    // Run a monitoring scan
    runScan: async (scanType: string = 'manual'): Promise<ScanResult> => {
        const response = await apiClient.post('/price-monitor/scan', {
            scan_type: scanType,
        });
        return response.data;
    },

    // Get alerts
    getAlerts: async (
        page: number = 1,
        pageSize: number = 50,
        status?: string
    ): Promise<{ items: PriceDropAlert[]; total: number; page: number; page_size: number }> => {
        const params: any = { page, page_size: pageSize };
        if (status) params.status = status;

        const response = await apiClient.get('/price-monitor/alerts', { params });
        return response.data;
    },

    // Acknowledge an alert
    acknowledgeAlert: async (
        alertId: number,
        resolutionNotes?: string
    ): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post('/price-monitor/alerts/acknowledge', {
            alert_id: alertId,
            resolution_notes: resolutionNotes,
        });
        return response.data;
    },

    // Bulk delete alerts
    bulkDeleteAlerts: async (
        alertIds: number[]
    ): Promise<{ success: boolean; message: string }> => {
        const response = await apiClient.post('/price-monitor/alerts/bulk-delete', {
            alert_ids: alertIds,
        });
        return response.data;
    },

    // Get scan history
    getScanHistory: async (
        page: number = 1,
        pageSize: number = 50
    ): Promise<{ items: ScanHistoryItem[]; total: number; page: number; page_size: number }> => {
        const response = await apiClient.get('/price-monitor/scan-history', {
            params: { page, page_size: pageSize },
        });
        return response.data;
    },

    // Get statistics
    getStatistics: async (): Promise<MonitoringStatistics> => {
        const response = await apiClient.get('/price-monitor/statistics');
        return response.data;
    },
};
